import { createApi } from '@reduxjs/toolkit/query/react';
import { AppDispatch } from 'store';
import { v4 as uuid } from 'uuid';

import { apiSahBudgets } from '~constants/api';

import { Paginated, PaginatedRequest, Request, rtkBaseQueryWrapper, unwrap } from '~libs/axiosWrapper';

import { Supplement } from '~lookups/models';
import { Budget, BudgetActivitiesLog, Section, SectionItem } from '~support-at-home/models';

export const typePrefix = `clients-budgets`;

export type BudgetReq = Request & { id: string };

export type SectionReq = {
  revision_id?: string;
  client_id: number | undefined;
  package_fixed_id: number | undefined;
  start_date?: string;
  end_date?: string;
  ignore_sah_filters?: boolean;
};

/**
 * One service on the prefilled-services response, as the API's /services endpoint shapes it.
 *
 * The two effective dates are the contract this feature turns on: the API resolves each service
 * type's availability against the budget window and sends back the portion a line built from it
 * should carry. They are named here rather than read off an `any` because the fallback below is
 * silent - a renamed or missing field produces no compile error, no runtime error and no failed
 * request, just every new line dated to the whole budget again, which is the behaviour this ticket
 * exists to replace. See buildSections' tests.
 */
export interface PrefilledService {
  id: number;
  effective_start_date?: string | null;
  effective_end_date?: string | null;
}

export interface PrefilledCategory {
  id: string;
  services: PrefilledService[];
}

interface ContributionEntry {
  category_id: string;
  contrib_proportion: number | null;
}

interface BulkCopyEstimateRequest {
  start_date: string;
  end_date: string;
  client_ids?: number[];
  funding_type_ids?: number[];
  status_ids?: string[];
}

interface BulkCopyRequest {
  budget_ids: string[];
  start_date: string;
  end_date: string;
  target_revision_id?: string | null;
  /**
   * Carry services whose contribution category changes over the new dates into the category that
   * applies, rather than dropping them. Omitted means true - dropping them is the behaviour this
   * replaced, so it is not something a caller falls back to by saying nothing.
   */
  apply_category_moves?: boolean;
}

interface BulkCopyPreviewRequest {
  budget_ids: string[];
  start_date: string;
  end_date: string;
  target_revision_id?: string | null;
}

/** One service a copy carries into a different contribution category, as the preview reports it. */
export interface MovedServiceRow {
  service_type_id: string;
  service_type_name: string | null;
  from_category_id: string | null;
  from_category_name: string | null;
  to_category_id: string;
  to_category_name: string | null;
  /** The date the destination category takes effect for this service. */
  change_date: string | null;
}

export interface BudgetPreviewRow {
  budget_id: string;
  ref: string;
  client_name: string;
  package: string;
  funding_type: string;
  removed_count: number;
  /**
   * Services whose contribution category changes over the new dates. Reported apart from
   * `removed_count` and never folded into it: a moved service is still in the copied budget, and one
   * figure covering both is what stopped a provider telling a loss from a relocation.
   */
  moved_count: number;
  moved_services: MovedServiceRow[];
  price_missing_count: number;
  needs_review: boolean;
}

/**
 * A budget that cannot be copied at all — as opposed to BudgetPreviewRow's "copied, but needs review".
 * `reason` is the API's human-readable explanation (e.g. an unresolvable funding source) and is shown
 * verbatim in the wizard's failure table.
 */
export interface BudgetFailureRow {
  budget_id: string;
  ref: string | null;
  client_name: string | null;
  package: string | null;
  funding_type: string | null;
  reason: string;
  /**
   * Whether fixing the underlying data and retrying could copy this budget. `false` marks a terminal row —
   * an id that never resolved to a budget at all (deleted, or another company's), which the API deliberately
   * keeps out of `remaining_budget_ids` so a retry loop converges. Absent on the dry-run preview's failures,
   * where nothing has been attempted yet and there is no retry to offer; treat undefined as retryable.
   */
  retryable?: boolean;
}

export interface BulkCopyPreviewData {
  total: number;
  previewed: number;
  clean: number;
  needs_review: number;
  // clean + needs_review + failed === previewed
  failed: number;
  budgets: BudgetPreviewRow[];
  failures: BudgetFailureRow[];
}

export type BulkCopyRunStatus = 'running' | 'completed' | 'failed';

export interface BulkCopyRun {
  id: string;
  status: BulkCopyRunStatus;
  /** Size of THIS run's selection (a retry run covers only the outstanding budgets). */
  total_count: number;
  copied_count: number;
  needs_review_count: number;
  /**
   * Budgets skipped for a per-budget reason. NOT the size of the `failures` table — that also carries the
   * terminal unresolvable ids the API reports as `skipped_count`, so `failures.length` is the two combined.
   * Anything user-facing should count `failures`, not this.
   */
  failed_count: number;
  error_message: string | null;
  started_at: string;
  finished_at: string | null;
  /** Source budget ids still outstanding — the precise retry list. Omitted when there's nothing to retry. */
  remaining_budget_ids?: string[];
  /** Copied budgets. Only sent once the run is terminal (completed or failed). */
  budgets?: BudgetPreviewRow[];
  /**
   * Every budget that could not be copied — both the retryable per-budget failures and the terminal
   * unresolvable ids, distinguished by `retryable`. Sent for every status so problems show up live.
   */
  failures?: BudgetFailureRow[];
}

interface BulkSahPreviewRequest {
  budget_ids: string[];
}

export interface SahCandidate {
  id: string;
  label: string;
}

export interface BudgetSahPreview {
  budget_id: string;
  reference: string;
  client_name: string;
  start_date: string;
  end_date: string;
  funding_type_name: string;
  current_sah_budget_id: string | null;
  candidates: SahCandidate[];
}

export interface SahAssignment {
  budget_id: string;
  sah_budget_id: string | null;
}

interface BulkSahAssignRequest {
  assignments: SahAssignment[];
}

interface BulkSahAssignResult {
  assigned_count: number;
}

export interface CopyServicesReq {
  id: string;
  revision_id: string;
  client_id: number;
  package_fixed_id: number;
  start_date: string;
  end_date: string;
  ignore_sah_filters?: boolean;
  /**
   * Carry a service whose contribution category changes over the new dates into the category that
   * applies, rather than dropping it. Omitted means carry it - the drop is what this replaced.
   */
  apply_category_moves?: boolean;
}

export const budgetsApi = createApi({
  reducerPath: 'budgetsApi',
  baseQuery: rtkBaseQueryWrapper({ baseUrl: apiSahBudgets }),
  tagTypes: ['budgets', 'services', 'contributions', 'logs'],
  endpoints: (builder) => ({
    getBudgets: builder.query<Budget[], Request | void>({
      query: (params) => ({ url: '', method: 'GET', params }),
      providesTags: [{ type: 'budgets', id: 'LIST' }],
      transformResponse: (payload) => unwrap<Budget[]>(payload),
    }),

    getBudgetsPaginated: builder.query<Paginated<Budget>, PaginatedRequest>({
      query: (params) => ({ url: '', method: 'GET', params }),
      providesTags: [{ type: 'budgets', id: 'LIST' }],
      transformResponse: (payload) => unwrap<Paginated<Budget>>(payload),
    }),

    getBudget: builder.query<Budget, BudgetReq>({
      query: ({ id, ...rest }) => ({ url: `/${id}`, method: 'GET', params: rest }),
      providesTags: (_result, _error, { id }) => [{ type: 'budgets', id }],
      transformResponse: (payload) => unwrap<Budget>(payload),
    }),

    getBulkCopyEstimate: builder.query<Budget[], BulkCopyEstimateRequest>({
      query: (params) => ({ url: '/bulk-copy-estimate', method: 'GET', params }),
      transformResponse: (payload) => unwrap<Budget[]>(payload),
    }),

    saveBudget: builder.mutation<Budget, Partial<Budget>>({
      query: (body) => ({ url: '', method: 'POST', body }),
      invalidatesTags: (result) =>
        result?.id
          ? [
              { type: 'budgets', id: 'LIST' },
              { type: 'budgets', id: result.id },
            ]
          : [{ type: 'budgets', id: 'LIST' }],
      transformResponse: (payload) => unwrap<Budget>(payload),
    }),

    deleteBudget: builder.mutation({
      query: (id) => ({ url: `/${id}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'budgets', id: 'LIST' },
        { type: 'budgets', id },
      ],
      transformResponse: (payload) => unwrap<any>(payload),
    }),

    bulkUpdate: builder.mutation<any, any>({
      query: (body) => ({ url: `/bulk-update`, method: 'POST', body }),
      invalidatesTags: (_result) => [{ type: 'budgets', id: 'LIST' }],
      transformResponse: (payload) => unwrap<any>(payload),
    }),

    // Kicks off a server-driven copy run and returns its id — the copy itself is not done when
    // this resolves. The budgets list is invalidated later, when the run completes (see Review.tsx).
    bulkCopy: builder.mutation<{ runId: string }, BulkCopyRequest>({
      query: (body) => ({ url: `/bulk-copy`, method: 'POST', body }),
      transformResponse: (payload) => unwrap<{ runId: string }>(payload),
    }),

    bulkCopyPreview: builder.mutation<BulkCopyPreviewData, BulkCopyPreviewRequest>({
      query: (body) => ({ url: `/bulk-copy-preview`, method: 'POST', body }),
      transformResponse: (payload) => unwrap<BulkCopyPreviewData>(payload),
    }),

    getBulkCopyRun: builder.query<BulkCopyRun, string>({
      query: (runId) => ({ url: `/bulk-copy-runs/${runId}`, method: 'GET' }),
      transformResponse: (payload) => unwrap<BulkCopyRun>(payload),
    }),

    getActiveBulkCopyRun: builder.query<BulkCopyRun | null, void>({
      query: () => ({ url: `/bulk-copy-runs/active`, method: 'GET' }),
      transformResponse: (payload) => unwrap<BulkCopyRun | null>(payload),
    }),

    bulkSahPreview: builder.mutation<BudgetSahPreview[], BulkSahPreviewRequest>({
      query: (body) => ({ url: `/bulk-sah-preview`, method: 'POST', body }),
      transformResponse: (payload) => unwrap<BudgetSahPreview[]>(payload),
    }),

    bulkSahAssign: builder.mutation<BulkSahAssignResult, BulkSahAssignRequest>({
      query: (body) => ({ url: `/bulk-sah-assign`, method: 'POST', body }),
      invalidatesTags: [{ type: 'budgets', id: 'LIST' }],
      transformResponse: (payload) => unwrap<BulkSahAssignResult>(payload),
    }),

    recalculate: builder.mutation<any, any>({
      query: (body) => ({ url: `/recalculate`, method: 'POST', body }),
      transformResponse: (payload) => unwrap<Budget>(payload),
    }),

    getPrefilledServices: builder.query<PrefilledCategory[], Partial<SectionReq>>({
      query: (params) => ({ url: `/services`, method: 'GET', params }),
      providesTags: [{ type: 'services', id: 'LIST' }],
      transformResponse: (payload) => unwrap<PrefilledCategory[]>(payload),
    }),

    getFilteredServices: builder.query<any, Partial<SectionReq>>({
      query: (params) => ({ url: `/filtered-services`, method: 'GET', params }),
      providesTags: (_result, _error, args) => [
        {
          type: 'services',
          id: `${args.ignore_sah_filters ?? false}-${args.revision_id ?? ''}-${args.client_id ?? ''}-${args.package_fixed_id ?? ''}-${args.start_date ?? ''}-${args.end_date ?? ''}`,
        },
      ],
      transformResponse: (payload) => unwrap<any>(payload),
    }),

    getContributions: builder.query<any, Partial<SectionReq>>({
      query: (params) => ({ url: `/contributions`, method: 'GET', params }),
      providesTags: [{ type: 'contributions', id: 'LIST' }],
      transformResponse: (payload) => unwrap<any>(payload),
    }),

    getLogs: builder.query<BudgetActivitiesLog[], BudgetReq>({
      query: ({ id, ...rest }) => ({ url: `/${id}/logs`, method: 'GET', params: rest }),
      providesTags: [{ type: 'logs', id: 'LIST' }],
      transformResponse: (payload) => unwrap<BudgetActivitiesLog[]>(payload),
    }),

    getCopyServices: builder.query<{ sections: Section[]; supplements: Supplement[] }, CopyServicesReq>({
      query: ({ id, ...params }) => ({ url: `/${id}/copy`, method: 'GET', params }),
      transformResponse: (payload) => unwrap<{ sections: Section[]; supplements: Supplement[] }>(payload),
    }),

    updateRevision: builder.mutation<Budget, { id: string }>({
      query: ({ id }) => ({ url: `/${id}/update-revision`, method: 'POST' }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'budgets', id: 'LIST' },
        { type: 'budgets', id },
      ],
      transformResponse: (payload) => unwrap<Budget>(payload),
    }),
  }),
});

// AsyncThunk for orchestrating multiple requests to build sections data
export const buildSections =
  ({ revision_id, client_id, package_fixed_id, start_date, end_date }: Partial<SectionReq>) =>
  async (dispatch: AppDispatch) => {
    if (!package_fixed_id) {
      console.warn('buildSections skipped: missing package_fixed_id');
      return [];
    }

    try {
      // Run both service + contribution requests in parallel
      const [prefilled, contributions] = await Promise.all([
        dispatch(
          budgetsApi.endpoints.getPrefilledServices.initiate({
            revision_id,
            client_id,
            package_fixed_id,
            start_date,
            end_date,
          }),
        ).unwrap(),
        start_date && end_date
          ? dispatch(
              budgetsApi.endpoints.getContributions.initiate({
                client_id,
                package_fixed_id,
                start_date,
                end_date,
              }),
            ).unwrap()
          : Promise.resolve([]),
      ]);

      // Merge the data together
      const sections = prefilled.map((category: PrefilledCategory) => {
        const contrib = (contributions as ContributionEntry[]).find((c) => c.category_id === category.id);
        return {
          id: uuid(),
          category_id: category.id,
          category,
          contrib_proportion: contrib ? contrib.contrib_proportion : null,
          items: (category.services ?? []).map((service: PrefilledService) => ({
            id: uuid(),
            category_id: category.id,
            service_type_id: service.id,
            // The part of the budget window this service is actually available for. A service that
            // ends part way through gets a line dated to the portion before it ends, and its
            // replacement - which arrives as its own entry under its own category - gets the rest.
            // Falls back to the budget's own dates for a service that spans the whole window.
            start_date: service.effective_start_date ?? start_date,
            end_date: service.effective_end_date ?? end_date,
            week_days: [],
            include_public_holidays: false,
            is_external_provider: false,
          })),
          totals: { cost: 0, contribution: 0, govSubsidy: 0 },
        };
      });
      return sections;
    } catch (error) {
      console.error('buildSections failed:', error);
      return [];
    }
  };

// Thunk for building copy sections — fetches reconciled data from API, merges with contributions
export const buildCopySections =
  ({
    id,
    revision_id,
    client_id,
    package_fixed_id,
    start_date,
    end_date,
    ignore_sah_filters,
    apply_category_moves,
  }: CopyServicesReq) =>
  async (dispatch: AppDispatch) => {
    try {
      const [copyData, contributions] = await Promise.all([
        dispatch(
          budgetsApi.endpoints.getCopyServices.initiate({
            id,
            revision_id,
            client_id,
            package_fixed_id,
            start_date,
            end_date,
            ignore_sah_filters,
            apply_category_moves,
          }),
        ).unwrap(),
        start_date && end_date
          ? dispatch(
              budgetsApi.endpoints.getContributions.initiate({
                client_id,
                package_fixed_id,
                start_date,
                end_date,
              }),
            ).unwrap()
          : Promise.resolve([]),
      ]);

      const sections = (copyData.sections ?? []).map((section: Section) => {
        const contrib = contributions.find((c: { category_id: string }) => c.category_id === section.category_id);
        return {
          ...section,
          id: uuid(),
          contrib_proportion: contrib ? contrib.contrib_proportion : (section.contrib_proportion ?? null),
          items: (section.items ?? []).map((item: SectionItem) => ({
            ...item,
            id: uuid(),
          })),
        };
      });

      return { sections, supplements: copyData.supplements ?? [] };
    } catch (error) {
      console.error('buildCopySections failed:', error);
      throw error;
    }
  };

export const {
  useGetBudgetsQuery,
  useLazyGetBudgetsQuery,
  useGetBudgetsPaginatedQuery,
  useGetBudgetQuery,
  useGetBulkCopyEstimateQuery,
  useLazyGetBudgetQuery,
  useSaveBudgetMutation,
  useDeleteBudgetMutation,
  useBulkUpdateMutation,
  useBulkCopyMutation,
  useRecalculateMutation,
  useGetPrefilledServicesQuery,
  useGetFilteredServicesQuery,
  useLazyGetFilteredServicesQuery,
  useGetContributionsQuery,
  useGetLogsQuery,
  useUpdateRevisionMutation,
  useBulkSahPreviewMutation,
  useBulkSahAssignMutation,
  useBulkCopyPreviewMutation,
  useGetBulkCopyRunQuery,
  useLazyGetBulkCopyRunQuery,
  useGetActiveBulkCopyRunQuery,
  useLazyGetActiveBulkCopyRunQuery,
} = budgetsApi;
